import { useTranslation } from 'react-i18next'
import { Design } from '../../../core/models/entities/Design'
import { DesignStatus } from '../../../core/models/entities/enums/DesignStatus'
import { ApprovalStatus } from '../../../core/models/entities/Approval'
import { GetOverallApprovalStatus } from '../../../core/models/entities/BaseLogo'
import { ApprovalDisplay, ApprovalListDisplay } from '../Approval/ApprovalDisplay'
import { approvalHasPendingParticipant } from '../../../utils/functions/orderability'
import { enviroment } from '../../../enviroment'

// Flag REACT_APP_DESIGN_STATUS_FOLLOWS_APPROVAL_ENABLED (Nichlas 17/10:
// «statusser kommer ud som godkendt selvom den står i korrektur i nav – den
// burde vel komme som afventer»): the Status cell reads "AWAITING · Vendor /
// Customer / End Customer" while the design is still in its approval -
// whether design.status is done or approval. The element sits in the list
// row for every status, and in the design popup only for status approval /
// rejected (the popup's done branch shows no status text), so a done +
// vendor Pending row changes in the list cell only. Today
// ApprovalListDisplay shows "APPROVED" for a done design whose ONLY
// participant is the vendor - also when that vote is still Pending (the JS
// upload's done + vendor Pending) - and the vendor's approval overrides a
// pending end customer in the overall status; a design in status approval
// with the vendor Approved and no customer vote (the hung state, only Delete
// in the row) reads "APPROVED BY · Vendor". Flag off = today's rendering,
// byte for byte. Same rule as the orderability guard (isDesignOrderable):
// what is shown as AWAITING is what cannot be ordered yet.
//   done:     AWAITING when a participant is Pending.
//   approval: AWAITING always - the design is in «korrektur» by definition.
// A rejection always wins: any Rejected participant keeps today's REJECTED
// header, also next to a Pending participant. Where AWAITING overrides an
// overall Approved (hung state, approved vendor + pending end customer),
// ApprovalDisplay spells out each participant's state for screen readers.
function awaitingHeader(design: Design): boolean {
  if (!enviroment.designStatusFollowsApprovalEnabled) return false
  if (approvalHasRejectedParticipant(design.approval)) return false
  if (design.status === DesignStatus.Approval) return true
  return approvalHasPendingParticipant(design.approval)
}

// Null group = nobody asked = no rejection (explicit, instead of leaning on
// GetOverallApprovalStatus' defensive null branch).
function approvalHasRejectedParticipant(approval: Design['approval']): boolean {
  if (!approval) return false
  return GetOverallApprovalStatus(approval) === ApprovalStatus.Rejected
}

export function DesignStatusElement({ design }: { design: Design }) {
  const { t } = useTranslation()
  switch (design.status) {
    case DesignStatus.Unknown:
      return <p className={`mb-1 font-semibold text-xs text-gray-600`}>{t("compLogoDesign.statusUnknown")}</p>
    case DesignStatus.Draft:
      return <p className={`mb-1 font-semibold text-xs text-orange-400`}>{t("compLogoDesign.statusDraft")}</p>
    case DesignStatus.Rejected:
      return <ApprovalDisplay approval={design.approval!} />
    case DesignStatus.Approval:
      if (awaitingHeader(design)) {
        return <ApprovalDisplay approval={design.approval!} headerStatus={ApprovalStatus.Pending} />
      }
      return <ApprovalDisplay approval={design.approval!} />
    case DesignStatus.Done:
      if (awaitingHeader(design)) {
        return <ApprovalDisplay approval={design.approval!} headerStatus={ApprovalStatus.Pending} />
      }
      return <ApprovalListDisplay approval={design.approval!} />
  }
}
